/**
 * useInterviewRecorder -- captures the interview as a DUAL-STREAM recording
 * and streams both halves to the interview-service as they're produced.
 *
 * Design choices:
 * - We mix the candidate mic + the AI playback audio together client-side
 *   into one MediaStream so the resulting cam WebM has both sides of the
 *   call. Server-side mixing would mean ffmpeg in a Celery worker -- way
 *   too heavy for what is effectively "save the conversation".
 * - Video pipeline: TWO independent MediaRecorders run in parallel.
 *     a) `cam` recorder: candidate webcam + mixed audio  -> kind=cam blob
 *     b) `screen` recorder: screen-share video, no audio -> kind=screen blob
 *   They live as TWO separate WebM files for the same session. That way
 *   the review player can overlay them in picture-in-picture and let the
 *   reviewer swap which feed is the main vs the small inset at playback
 *   time -- impossible with a baked-in canvas composite, which was the
 *   previous design.
 * - We deliberately do NOT try to keep the two streams perfectly
 *   timestamp-aligned -- they start within ~50ms of each other and the
 *   playback hook nudges them back to the master clock if they drift.
 * - MediaRecorder fires `dataavailable` every `CHUNK_MS`; each chunk is
 *   POSTed as raw bytes to interview-service with `?kind=cam|screen` so
 *   the backend knows which Append Blob to append to. Partial recordings
 *   still play (DSGVO + UX win).
 * - Uploads are serialized PER stream via two tiny queues: we never want
 *   two appends in flight on the same blob because Append Blob ordering
 *   is per-request. The cam and screen queues run independently.
 * - If the user revokes the screen mid-interview, the screen recorder
 *   stops cleanly and we finalize that blob alone; cam keeps rolling.
 *   If the user shares a screen later in the session, we (re-)init the
 *   screen blob and start a fresh screen recorder.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE, type ApiCall } from '../api/interview';

const CHUNK_MS = 3000;
const PREFERRED_MIME = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];
const PREFERRED_MIME_VIDEO_ONLY = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
];

export type RecorderState =
  | 'idle'
  | 'initializing'
  | 'recording'
  | 'stopping'
  | 'finalized'
  | 'error';

/**
 * Kept for backward compat with consumers (InterviewRoom). With dual-stream
 * recording, layout is purely a PLAYBACK concern -- both feeds are recorded
 * raw and the reviewer picks the layout when watching. The value is unused
 * by the recorder itself.
 */
export type RecorderLayout = 'pip-screen' | 'pip-cam' | 'split';

export interface UseRecorderArgs {
  sessionId: string | null;
  cameraStream: MediaStream | null;
  /** Web Audio context owned by the interview audio hook -- we tap AI playback from here. */
  aiAudioContext: AudioContext | null;
  /** Node that the AI playback fans out from. We pull a copy of its output for the recording mix. */
  aiAudioTapNode: AudioNode | null;
  apiCall: ApiCall;
  /**
   * Anonymous warm-intro (Lite-ATS): when set, recording endpoints are hit
   * via /sessions/by-token/{anonToken}/recording/* using plain fetch (no JWT)
   * instead of the authenticated /sessions/{id}/recording/* path.
   */
  anonToken?: string | null;
  /** When true, the hook starts recording as soon as the camera stream + AI context are ready. */
  enabled: boolean;
  /**
   * When true, the template/mock expects a screen-share blob in addition
   * to the cam blob. If a screen stream is provided we'll record it; if
   * it later goes away we finalize the screen blob alone. When false the
   * `screenStream` argument is ignored -- only the cam blob is produced.
   * Set this from the template's `enable_screen_share` flag.
   */
  useComposite?: boolean;
  /**
   * Screen-share stream (from navigator.mediaDevices.getDisplayMedia).
   * Only consumed when `useComposite` is true.
   */
  screenStream?: MediaStream | null;
}

export function useInterviewRecorder({
  sessionId,
  cameraStream,
  aiAudioContext,
  aiAudioTapNode,
  apiCall,
  anonToken = null,
  enabled,
  useComposite = false,
  screenStream = null,
}: UseRecorderArgs) {
  // Anonymous take-path uses plain fetch + the by-token recording routes.
  // Authenticated path is byte-identical to before (anonToken null).
  const recFetch = useCallback<ApiCall>(
    (url, opts) => (anonToken ? fetch(url, opts) : apiCall(url, opts)),
    [anonToken, apiCall],
  );
  const recPath = useCallback(
    (suffix: string) =>
      anonToken
        ? `${API_BASE}/api/v1/interview/sessions/by-token/${anonToken}/recording/${suffix}`
        : `${API_BASE}/api/v1/interview/sessions/${sessionId}/recording/${suffix}`,
    [anonToken, sessionId],
  );
  const [state, setState] = useState<RecorderState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  // 'ok' = chunks landing normally; 'stalled' = at least one chunk
  // has been retrying for >15s; 'offline' = navigator.onLine reports
  // we're disconnected. The UI uses this to show a banner.
  const [uploadHealth, setUploadHealth] = useState<'ok' | 'stalled' | 'offline'>('ok');

  // --- Backwards-compat UI state (no recorder side-effect) -----------
  // These two are still read by the live header pill; on dual-stream
  // they have no recording-time meaning, but we keep them so existing
  // callers don't have to change their JSX in the same release. The
  // values flow through unchanged.
  const [splitRatio, setSplitRatio] = useState<number>(0.32);
  const [layout, setLayout] = useState<RecorderLayout>('pip-screen');

  // --- Cam pipeline --------------------------------------------------
  const camRecorderRef = useRef<MediaRecorder | null>(null);
  const camUploadQueueRef = useRef<Promise<void>>(Promise.resolve());
  const camInitializedRef = useRef(false);
  // Last time we observed cam MediaRecorder produce a non-empty chunk.
  // Used by the watchdog to detect a silently stalled recorder (Chrome
  // suspends MediaRecorder when the tab is backgrounded for too long
  // and on macOS sleep, never resuming on its own). 0 = no chunk yet.
  const camLastChunkAtRef = useRef<number>(0);
  const camStoppingRef = useRef(false);

  // --- Screen pipeline ----------------------------------------------
  // The screen recorder is only created once a screen stream is
  // actually live. If the candidate never shares, no screen blob is
  // ever opened on the server side either.
  const scrRecorderRef = useRef<MediaRecorder | null>(null);
  const scrUploadQueueRef = useRef<Promise<void>>(Promise.resolve());
  const scrInitializedRef = useRef(false);
  // Track which screen stream the recorder was attached to so we don't
  // restart on the SAME stream when React re-renders.
  const scrAttachedStreamRef = useRef<MediaStream | null>(null);
  const scrLastChunkAtRef = useRef<number>(0);
  const scrStoppingRef = useRef(false);

  // --- Audio mix -----------------------------------------------------
  const mixCtxRef = useRef<AudioContext | null>(null);
  const mixDestRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const camMixedStreamRef = useRef<MediaStream | null>(null);

  // Build the audio mix (candidate mic + AI playback) into a single
  // destination. Returns the destination's audio tracks. Called once
  // per session -- subsequent calls are a no-op.
  const buildAudioMix = useCallback((): MediaStreamTrack[] => {
    if (!cameraStream) return [];
    if (mixDestRef.current) {
      return mixDestRef.current.stream.getAudioTracks();
    }
    const ctx =
      aiAudioContext ??
      new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    mixCtxRef.current = ctx;
    const dest = ctx.createMediaStreamDestination();
    mixDestRef.current = dest;
    try {
      const candidateSource = ctx.createMediaStreamSource(cameraStream);
      candidateSource.connect(dest);
    } catch (e) {
      console.warn('useInterviewRecorder: candidate mic tap failed', e);
    }
    if (aiAudioTapNode) {
      try {
        aiAudioTapNode.connect(dest);
      } catch (e) {
        console.warn('useInterviewRecorder: AI tap node connect failed', e);
      }
    }
    return dest.stream.getAudioTracks();
  }, [cameraStream, aiAudioContext, aiAudioTapNode]);

  // ----------------------------------------------------------------
  // Chunk upload (parametrized by kind so cam + screen share the same
  // retry / queueing logic, just against different blobs).
  // ----------------------------------------------------------------

  // Resilient retry: WebM is a sequential format -- once a chunk is
  // missing, every chunk after it is corrupt at the cluster level
  // even if it uploads successfully. So "give up" on a chunk = give
  // up on the WHOLE rest of the recording from that point. The old
  // 31s budget killed multi-minute interviews on a 60s WLAN blip.
  //
  // New strategy:
  //   - 12 attempts with capped exponential backoff (max 60s)
  //   - When navigator.onLine === false, skip the timed wait and
  //     `await` the 'online' event instead (instant retry as soon
  //     as the laptop sees a network again)
  //   - Total worst-case patience ~ 12 min, which covers laptop
  //     sleep, train tunnels, brief WiFi handover -- all the cases
  //     where the user genuinely comes back to a live interview
  const CHUNK_RETRY_DELAYS_MS = [
    1_000, 2_000, 4_000, 8_000, 16_000, 30_000,
    60_000, 60_000, 60_000, 60_000, 60_000, 60_000,
  ];

  const waitForOnline = (maxMs: number): Promise<void> =>
    new Promise<void>((resolve) => {
      if (typeof navigator === 'undefined' || navigator.onLine !== false) {
        resolve();
        return;
      }
      const cleanup = () => {
        window.removeEventListener('online', onOnline);
        clearTimeout(t);
      };
      const onOnline = () => { cleanup(); resolve(); };
      const t = setTimeout(() => { cleanup(); resolve(); }, maxMs);
      window.addEventListener('online', onOnline);
    });

  const uploadChunkWithRetry = useCallback(
    async (chunk: Blob, kind: 'cam' | 'screen'): Promise<boolean> => {
      if (!sessionId) return false;
      const buf = await chunk.arrayBuffer();
      const url = `${recPath('chunk')}?kind=${kind}`;
      for (let attempt = 0; attempt <= CHUNK_RETRY_DELAYS_MS.length; attempt += 1) {
        // If the browser already knows we're offline, don't even
        // try -- just wait for an 'online' event (or up to 60s).
        // This avoids burning retry attempts on certain-failure
        // requests during a laptop sleep / known WLAN drop.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          setUploadHealth('offline');
          await waitForOnline(60_000);
        } else if (attempt >= 2) {
          setUploadHealth('stalled');
        }
        try {
          const resp = await recFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/octet-stream' },
            body: buf,
          });
          if (resp.ok) {
            // Only the cam stream's bytes feed the "uploaded so far"
            // UI -- the screen stream doubles the count and confuses
            // anyone watching. Cam is the canonical progress signal.
            if (kind === 'cam') {
              const json = (await resp.json().catch(() => null)) as
                | { total?: number; bytes?: number }
                | null;
              if (json?.total) setUploadedBytes(json.total);
            }
            // Successful upload -- reset any prior stalled / offline
            // banner so the candidate sees the recording is healthy
            // again.
            if (attempt > 0) setUploadHealth('ok');
            else setUploadHealth((h) => (h === 'ok' ? h : 'ok'));
            return true;
          }
          if (
            resp.status >= 400 &&
            resp.status < 500 &&
            resp.status !== 408 &&
            resp.status !== 425 &&
            resp.status !== 429
          ) {
            console.warn(
              `useInterviewRecorder[${kind}]: chunk permanently rejected`,
              resp.status,
              await resp.text().catch(() => ''),
            );
            return false;
          }
        } catch (e) {
          if (attempt === CHUNK_RETRY_DELAYS_MS.length) {
            console.warn(
              `useInterviewRecorder[${kind}]: chunk gave up after ${CHUNK_RETRY_DELAYS_MS.length + 1} attempts`,
              e,
            );
          }
        }
        if (attempt < CHUNK_RETRY_DELAYS_MS.length) {
          // Race the timed backoff against an 'online' event so we
          // retry instantly when the network comes back rather than
          // sitting through the full backoff.
          await Promise.race([
            new Promise<void>((r) => setTimeout(r, CHUNK_RETRY_DELAYS_MS[attempt])),
            new Promise<void>((r) => {
              const onOnline = () => {
                window.removeEventListener('online', onOnline);
                r();
              };
              window.addEventListener('online', onOnline);
            }),
          ]);
        }
      }
      return false;
    },
    [recFetch, recPath, sessionId],
  );

  const pushCam = useCallback(
    (chunk: Blob) => {
      if (!sessionId) return;
      camUploadQueueRef.current = camUploadQueueRef.current
        .then(() => uploadChunkWithRetry(chunk, 'cam'))
        .then((ok) => {
          if (!ok) {
            console.warn(
              'useInterviewRecorder[cam]: dropping chunk after retries (recording will be partial from here)',
            );
          }
        })
        .catch((e) => console.warn('useInterviewRecorder[cam]: queue error', e));
    },
    [sessionId, uploadChunkWithRetry],
  );

  const pushScreen = useCallback(
    (chunk: Blob) => {
      if (!sessionId) return;
      scrUploadQueueRef.current = scrUploadQueueRef.current
        .then(() => uploadChunkWithRetry(chunk, 'screen'))
        .then((ok) => {
          if (!ok) {
            console.warn(
              'useInterviewRecorder[screen]: dropping chunk after retries (recording will be partial from here)',
            );
          }
        })
        .catch((e) => console.warn('useInterviewRecorder[screen]: queue error', e));
    },
    [sessionId, uploadChunkWithRetry],
  );

  // ----------------------------------------------------------------
  // Stream startup
  // ----------------------------------------------------------------

  const pickMime = useCallback((candidates: string[]): string | null => {
    if (typeof MediaRecorder === 'undefined') return null;
    return candidates.find((m) => MediaRecorder.isTypeSupported(m)) ?? null;
  }, []);

  // Start the cam pipeline. CRITICAL: we create + start the
  // MediaRecorder BEFORE awaiting /recording/init. Reason: Chrome
  // will end a getUserMedia / getDisplayMedia track that has no
  // active sinks. During the network round-trip of /recording/init
  // (typically 100-500ms) the track might temporarily have no sink
  // (TechCheck's preview just unmounted, MediaRecorder not yet
  // attached) -- which Chrome interprets as "nobody wants this
  // track" and silently ends it. By attaching MediaRecorder first
  // we guarantee a continuous sink. Chunk uploads use a per-stream
  // gate that holds until init confirms; if init fails the gate
  // never opens and the chunks are dropped (recorder still runs,
  // it just doesn't upload).
  const startCam = useCallback(async () => {
    if (!sessionId || !cameraStream || camInitializedRef.current) return;
    camInitializedRef.current = true;

    const audioTracks = buildAudioMix();
    const mixed = new MediaStream();
    audioTracks.forEach((t) => mixed.addTrack(t));
    cameraStream.getVideoTracks().forEach((t) => mixed.addTrack(t));
    camMixedStreamRef.current = mixed;

    const mime = pickMime(PREFERRED_MIME);
    if (!mime) {
      camInitializedRef.current = false;
      throw new Error('Browser does not support WebM MediaRecorder');
    }
    const rec = new MediaRecorder(mixed, {
      mimeType: mime,
      videoBitsPerSecond: 1_200_000,
    });
    camRecorderRef.current = rec;

    // Init gate: chunk uploads chain off this so they don't race
    // ahead of the server-side append blob being created.
    let initResolved = false;
    let initOk = false;
    const initGate = (async () => {
      try {
        const initResp = await recFetch(
          `${recPath('init')}?kind=cam`,
          { method: 'POST' },
        );
        initOk = initResp.ok;
        if (!initResp.ok) {
          console.warn('recording/init[cam] failed: HTTP', initResp.status);
        }
      } catch (e) {
        console.warn('recording/init[cam] failed', e);
      } finally {
        initResolved = true;
      }
    })();

    rec.ondataavailable = (e) => {
      if (!e.data || e.data.size === 0) return;
      camLastChunkAtRef.current = Date.now();
      if (initResolved && !initOk) return; // init failed, drop quietly
      if (initResolved) {
        pushCam(e.data);
      } else {
        const chunk = e.data;
        void initGate.then(() => { if (initOk) pushCam(chunk); });
      }
    };
    rec.onerror = (e) => console.warn('MediaRecorder[cam] error', e);
    // If the recorder stops on its own (browser auto-suspend on tab
    // background, OS sleep, MediaStream death) the watchdog will
    // notice and respawn it on the next tick. We just clear the ref
    // so the watchdog doesn't fight a zombie recorder.
    rec.onstop = () => {
      if (camStoppingRef.current) return;
      console.warn('MediaRecorder[cam] stopped unexpectedly -- watchdog will respawn');
      if (camRecorderRef.current === rec) {
        camRecorderRef.current = null;
        camInitializedRef.current = false;
      }
    };
    rec.start(CHUNK_MS);
    camLastChunkAtRef.current = Date.now();

    // Surface init failure to the caller (start()) so it can flag
    // the recorder as broken if cam init didn't work.
    await initGate;
    if (!initOk) {
      camInitializedRef.current = false;
      throw new Error('recording/init[cam] failed');
    }
  }, [recFetch, recPath, buildAudioMix, cameraStream, pickMime, pushCam, sessionId]);

  // Start the screen pipeline once a screen stream is available. Safe
  // to call multiple times -- internal flag dedupes.
  //
  // CRITICAL: MediaRecorder is created + started BEFORE we await
  // /recording/init. Chrome ends a getDisplayMedia track that has
  // no active sinks; during the 100-500ms init round-trip the only
  // sink (TechCheck's preview <video>) has just been detached, so
  // without an immediate MediaRecorder attachment the track gets
  // auto-killed -- which then bubbles up as ended → screenStream
  // null → stopScreenAlone → finalize → 0.5 MB screen recording.
  // Attaching MediaRecorder first guarantees a continuous sink.
  const startScreen = useCallback(
    async (stream: MediaStream) => {
      if (!sessionId || scrInitializedRef.current) return;
      scrInitializedRef.current = true;
      scrAttachedStreamRef.current = stream;

      // The screen stream from getDisplayMedia may have an audio track
      // (system audio). We deliberately strip it: the AI voice is
      // already in the cam blob's audio mix; doubling it would create
      // an echo at playback. Use a fresh MediaStream that contains
      // only the video track.
      const videoOnly = new MediaStream();
      stream.getVideoTracks().forEach((t) => videoOnly.addTrack(t));

      const mime = pickMime(PREFERRED_MIME_VIDEO_ONLY);
      if (!mime) {
        scrInitializedRef.current = false;
        scrAttachedStreamRef.current = null;
        console.warn('useInterviewRecorder[screen]: no compatible mime -- skipping');
        return;
      }

      // Create + start MediaRecorder synchronously so the track keeps
      // an active sink across the init round-trip. Chunks fire into
      // a small in-memory buffer until init confirms; if init fails
      // (or returns the old-backend body shape) the buffer is dropped.
      const rec = new MediaRecorder(videoOnly, {
        mimeType: mime,
        videoBitsPerSecond: 2_500_000,
      });
      scrRecorderRef.current = rec;

      let initResolved = false;
      let initOk = false;
      const initGate = (async () => {
        try {
          const initResp = await recFetch(
            `${recPath('init')}?kind=screen`,
            { method: 'POST' },
          );
          if (!initResp.ok) {
            console.warn(
              'useInterviewRecorder[screen]: init failed HTTP',
              initResp.status,
              '-- dropping screen chunks',
            );
            return;
          }
          // Safety guard against an old backend that doesn't understand
          // the `kind` query param. The new backend ALWAYS echoes back
          // `kind` in its JSON body; the old one returns `{ ok: true }`
          // and silently writes to the same blob the cam stream is
          // appending to -- which would corrupt the cam WebM with
          // interleaved screen chunks. If we don't see `kind: "screen"`
          // we drop the screen chunks; the cam blob survives intact.
          try {
            const body = (await initResp.json().catch(() => null)) as { kind?: string } | null;
            if (!body || body.kind !== 'screen') {
              console.warn(
                'useInterviewRecorder[screen]: backend did not acknowledge kind=screen ' +
                '-- dropping chunks to avoid corrupting the cam blob. ' +
                'Backend likely needs the dual-stream deploy.',
              );
              return;
            }
          } catch (parseErr) {
            console.warn('useInterviewRecorder[screen]: could not parse init body', parseErr);
            return;
          }
          initOk = true;
        } catch (e) {
          console.warn('useInterviewRecorder[screen]: init request errored', e);
        } finally {
          initResolved = true;
        }
      })();

      rec.ondataavailable = (e) => {
        if (!e.data || e.data.size === 0) return;
        scrLastChunkAtRef.current = Date.now();
        if (initResolved && !initOk) return;
        if (initResolved) {
          pushScreen(e.data);
        } else {
          const chunk = e.data;
          void initGate.then(() => { if (initOk) pushScreen(chunk); });
        }
      };
      rec.onerror = (e) => console.warn('MediaRecorder[screen] error', e);
      rec.onstop = () => {
        if (scrStoppingRef.current) return;
        // Spurious stop -- watchdog will respawn iff the screen track
        // is still alive.
        if (scrRecorderRef.current === rec) {
          scrRecorderRef.current = null;
          scrInitializedRef.current = false;
        }
      };
      rec.start(CHUNK_MS);
      scrLastChunkAtRef.current = Date.now();

      // Surface init failure so the caller can decide whether to retry.
      // We DON'T tear down the recorder here -- it stays running so the
      // track keeps its sink. Without that, ending init also ends the
      // track which we just spent a lot of effort to prevent.
      await initGate;
      if (!initOk) {
        scrInitializedRef.current = false;
        // Leave the recorder running but with no upload destination.
        // It'll be cleaned up by stop() / stopScreenAlone() later.
      }
    },
    [recFetch, recPath, pickMime, pushScreen, sessionId],
  );

  // Finalize one stream's blob server-side. Idempotent: server returns
  // the current URL even if nothing new was appended.
  const finalizeStream = useCallback(
    async (kind: 'cam' | 'screen') => {
      if (!sessionId) return;
      try {
        const fin = await recFetch(
          `${recPath('finalize')}?kind=${kind}`,
          { method: 'POST' },
        );
        if (!fin.ok) {
          console.warn(`recording/finalize[${kind}] HTTP`, fin.status);
        }
      } catch (e) {
        console.warn(`recording/finalize[${kind}] failed`, e);
      }
    },
    [recFetch, recPath, sessionId],
  );

  // Stop the screen recorder alone. Used when the candidate revokes
  // their share mid-interview without ending the whole interview.
  const stopScreenAlone = useCallback(async () => {
    scrStoppingRef.current = true;
    const rec = scrRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        rec.addEventListener('stop', () => resolve(), { once: true });
        try { rec.stop(); } catch { resolve(); }
      });
    }
    await scrUploadQueueRef.current;
    await finalizeStream('screen');
    scrRecorderRef.current = null;
    scrInitializedRef.current = false;
    scrAttachedStreamRef.current = null;
    scrStoppingRef.current = false;
  }, [finalizeStream]);

  // ----------------------------------------------------------------
  // Public start / stop
  // ----------------------------------------------------------------

  const start = useCallback(async () => {
    if (!sessionId || !cameraStream) return;
    if (camInitializedRef.current) return;
    setState('initializing');
    setError(null);
    try {
      // Kick BOTH pipelines off in parallel rather than serialising
      // them with `await startCam(); startScreen()`. The serial version
      // left the screen track without a sink for the ~200ms cam-init
      // round-trip; Chrome then ended the track and the screen
      // recording finalized at ~5s.
      //
      // startCam / startScreen each attach their MediaRecorder
      // SYNCHRONOUSLY now (see their bodies). So Promise.all races
      // their /recording/init awaits in parallel and the sinks are
      // present from t=0 in both cases.
      const camPromise = startCam();
      const screenPromise = (useComposite && screenStream)
        ? startScreen(screenStream)
        : Promise.resolve();
      await Promise.all([camPromise, screenPromise]);
      setState('recording');
    } catch (e) {
      console.warn('useInterviewRecorder.start failed', e);
      setError(e instanceof Error ? e.message : String(e));
      setState('error');
    }
  }, [cameraStream, screenStream, sessionId, startCam, startScreen, useComposite]);

  const stop = useCallback(async () => {
    if (!sessionId) return;
    setState('stopping');
    camStoppingRef.current = true;
    scrStoppingRef.current = true;
    try {
      const cam = camRecorderRef.current;
      const scr = scrRecorderRef.current;
      const stopPromises: Promise<void>[] = [];
      // Force a final `dataavailable` BEFORE we stop, so the trailing
      // WebM cluster is captured even if the browser would otherwise
      // hand us a truncated buffer on `stop`. Some browsers (notably
      // Safari and Chrome after long idle) skip the final dataavailable
      // when stop() is called from an inactive-ish state, which leaves
      // the Append Blob with a partial cluster -- mkvmerge then falls
      // back to first-segment-only on remux (= 1-2 min replay).
      if (cam && cam.state === 'recording') {
        try { cam.requestData(); } catch { /* noop */ }
      }
      if (scr && scr.state === 'recording') {
        try { scr.requestData(); } catch { /* noop */ }
      }
      if (cam && cam.state !== 'inactive') {
        stopPromises.push(
          new Promise<void>((resolve) => {
            cam.addEventListener('stop', () => resolve(), { once: true });
            try { cam.stop(); } catch { resolve(); }
            // Hard ceiling: don't wait forever for the stop event in
            // edge browsers (the upstream 45s race already caps the
            // whole stop() call, this is just a safety net per-rec).
            setTimeout(resolve, 8000);
          }),
        );
      }
      if (scr && scr.state !== 'inactive') {
        stopPromises.push(
          new Promise<void>((resolve) => {
            scr.addEventListener('stop', () => resolve(), { once: true });
            try { scr.stop(); } catch { resolve(); }
            setTimeout(resolve, 8000);
          }),
        );
      }
      await Promise.all(stopPromises);

      await Promise.all([camUploadQueueRef.current, scrUploadQueueRef.current]);

      const finalizeCalls: Promise<void>[] = [finalizeStream('cam')];
      if (scrInitializedRef.current) {
        finalizeCalls.push(finalizeStream('screen'));
      }
      await Promise.all(finalizeCalls);

      setState('finalized');
    } catch (e) {
      console.warn('useInterviewRecorder.stop failed', e);
      setError(e instanceof Error ? e.message : String(e));
      setState('error');
    } finally {
      try {
        mixDestRef.current?.disconnect();
      } catch { /* ignore */ }
      mixDestRef.current = null;
      camMixedStreamRef.current = null;
      camRecorderRef.current = null;
      scrRecorderRef.current = null;
      camInitializedRef.current = false;
      scrInitializedRef.current = false;
      scrAttachedStreamRef.current = null;
    }
  }, [finalizeStream, sessionId]);

  // ----------------------------------------------------------------
  // Watchdog: respawn cam MediaRecorder if it goes silent.
  //
  // Failure modes the watchdog covers:
  //   - Chrome's "tab backgrounded too long" silently throttles
  //     MediaRecorder to no more dataavailable events
  //   - macOS sleep + wake puts MediaRecorder in 'inactive' with no
  //     resume signal
  //   - A wifi handover briefly kills the MediaStream tracks; the
  //     audio mix's tap loses its source and the recorder produces
  //     zero-byte chunks
  //   - MediaRecorder.onstop fired without our consent (browser
  //     suspended for resource pressure)
  //
  // Cadence: every 5s we check (a) does the recorder still exist
  // and is .state === 'recording', (b) was a non-empty chunk emitted
  // in the last STALL_MS. If either fails we tear down and respawn
  // off the SAME camera stream (server-side blob is idempotent --
  // append continues from the existing offset).
  //
  // Server-side blob continues across respawn: the WebM written by
  // MediaRecorder is segmented (each new MediaRecorder writes a
  // brand new WebM header). Append Blob doesn't care -- we get a
  // single blob that contains a sequence of self-contained WebM
  // segments. The remux step (mkvmerge) transparently stitches
  // consecutive WebM segments into a single playable file. So we
  // get the FULL recording even across a respawn.
  // ----------------------------------------------------------------
  const STALL_MS = 12_000;
  const camRespawningRef = useRef(false);
  const respawnCamRef = useRef<() => Promise<void>>(async () => {});
  respawnCamRef.current = useCallback(async () => {
    if (!sessionId || !cameraStream) return;
    if (camStoppingRef.current) return;
    if (camRespawningRef.current) return;
    camRespawningRef.current = true;
    // Don't respawn if camera track is dead -- nothing to record from.
    const camTracks = cameraStream.getVideoTracks();
    if (camTracks.length === 0 || camTracks.every((t) => t.readyState === 'ended')) {
      console.warn('useInterviewRecorder: cam track ended, cannot respawn');
      return;
    }

    console.warn('useInterviewRecorder[cam]: respawning MediaRecorder');

    try {
      // Tear down the old recorder if any. We mark 'stopping' briefly
      // so the onstop handler doesn't fight us.
      const old = camRecorderRef.current;
      if (old) {
        camStoppingRef.current = true;
        try {
          if (old.state !== 'inactive') {
            await new Promise<void>((resolve) => {
              old.addEventListener('stop', () => resolve(), { once: true });
              try { old.stop(); } catch { resolve(); }
              // Fail-safe: don't wait forever for stop event.
              setTimeout(resolve, 1500);
            });
          }
        } finally {
          camStoppingRef.current = false;
          camRecorderRef.current = null;
        }
      }

      // Build a fresh MediaRecorder on the existing mixed stream
      // (audio mix + cam video). We deliberately re-use the existing
      // mix destination so the audio doesn't rebuild and lose pending
      // AI playback samples.
      const mixed = camMixedStreamRef.current;
      if (!mixed) {
        console.warn('useInterviewRecorder[cam]: no mixed stream to respawn from');
        return;
      }

      const mime = pickMime(PREFERRED_MIME);
      if (!mime) return;
      const rec = new MediaRecorder(mixed, {
        mimeType: mime,
        videoBitsPerSecond: 1_200_000,
      });
      camRecorderRef.current = rec;

      rec.ondataavailable = (e) => {
        if (!e.data || e.data.size === 0) return;
        camLastChunkAtRef.current = Date.now();
        pushCam(e.data);
      };
      rec.onerror = (e) => console.warn('MediaRecorder[cam] error after respawn', e);
      rec.onstop = () => {
        if (camStoppingRef.current) return;
        console.warn('MediaRecorder[cam] stopped unexpectedly after respawn');
        if (camRecorderRef.current === rec) {
          camRecorderRef.current = null;
          camInitializedRef.current = false;
        }
      };
      try {
        rec.start(CHUNK_MS);
        camLastChunkAtRef.current = Date.now();
        camInitializedRef.current = true;
      } catch (e) {
        console.warn('useInterviewRecorder[cam]: respawn start() failed', e);
      }
    } finally {
      camRespawningRef.current = false;
    }
  }, [cameraStream, pickMime, pushCam, sessionId]);

  useEffect(() => {
    if (state !== 'recording') return;
    const tick = setInterval(() => {
      if (state !== 'recording') return;
      if (camStoppingRef.current) return;
      const rec = camRecorderRef.current;
      const lastChunk = camLastChunkAtRef.current;
      const sinceLast = lastChunk > 0 ? Date.now() - lastChunk : 0;
      const recDead = !rec || rec.state === 'inactive';
      const stalled = lastChunk > 0 && sinceLast > STALL_MS;
      if (recDead || stalled) {
        console.warn(
          'useInterviewRecorder[cam] watchdog: respawning (recDead=%s stalled=%s sinceLast=%sms)',
          recDead, stalled, sinceLast,
        );
        void respawnCamRef.current();
      } else if (rec && rec.state === 'recording') {
        // Force a chunk flush every tick -- this also serves as a
        // canary: if rec.requestData() doesn't trigger ondataavailable
        // within STALL_MS the next tick will respawn.
        try { rec.requestData(); } catch { /* noop */ }
      }
    }, 5000);
    return () => clearInterval(tick);
  }, [state]);

  // Surface visibility / online events as immediate watchdog ticks.
  // Without this we have to wait up to 5s after wake-up before the
  // periodic interval fires; that 5s gap on a long sleep usually
  // means another stalled chunk.
  useEffect(() => {
    if (state !== 'recording') return;
    const onChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        const rec = camRecorderRef.current;
        if (!rec || rec.state === 'inactive') {
          void respawnCamRef.current();
        } else {
          try { rec.requestData(); } catch { /* noop */ }
        }
      }
    };
    const onOnline = () => {
      const rec = camRecorderRef.current;
      if (!rec || rec.state === 'inactive') {
        void respawnCamRef.current();
      }
    };
    document.addEventListener('visibilitychange', onChange);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onChange);
      window.removeEventListener('online', onOnline);
    };
  }, [state]);

  // Detect if the camera track itself dies mid-session. Without this
  // listener the watchdog above would just keep refusing to respawn
  // (cam track ended) and the candidate gets no signal. We log loudly
  // and surface 'error' so the InterviewRoom can show a reconnect
  // banner if it wants.
  useEffect(() => {
    if (state !== 'recording') return;
    if (!cameraStream) return;
    const tracks = cameraStream.getVideoTracks();
    const onEnded = () => {
      console.warn(
        'useInterviewRecorder: camera video track ended mid-session -- recording will stop here',
      );
      setError('camera_track_ended');
    };
    tracks.forEach((t) => t.addEventListener('ended', onEnded));
    return () => {
      tracks.forEach((t) => t.removeEventListener('ended', onEnded));
    };
  }, [cameraStream, state]);

  // ----------------------------------------------------------------
  // Hot-swap: react to the screen stream coming or going mid-interview.
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!useComposite) return;
    if (state !== 'recording' && state !== 'initializing') return;

    const currentlyAttached = scrAttachedStreamRef.current;

    if (screenStream && screenStream !== currentlyAttached) {
      // A new screen stream arrived (initial share, or candidate
      // re-shared after revoking). Stop any old recorder, then start
      // fresh -- the server will create a separate blob if `screen`
      // wasn't initialized yet, or simply append more bytes if it was.
      // (Initializing twice on the same blob is idempotent on the
      // server side; we still flip the flag back so startScreen runs.)
      if (currentlyAttached && scrRecorderRef.current) {
        void stopScreenAlone();
      }
      // Fire-and-forget; startScreen guards itself with a flag.
      void startScreen(screenStream);
      return;
    }

    if (!screenStream && currentlyAttached) {
      // The parent flagged screenStream as null. Most of the time this
      // means the candidate revoked the share -- but we've also seen
      // it fire spuriously when TechCheck unmounts during the
      // lobby -> in_call transition (Chrome briefly considers the
      // track "sinkless" and our ended-listener cascades to null).
      // Verify the track is ACTUALLY ended before tearing down the
      // recording. If it's still 'live' the parent's state just
      // flickered and we keep recording.
      const allEnded = currentlyAttached.getVideoTracks().every(
        (t) => t.readyState === 'ended',
      );
      if (allEnded) {
        void stopScreenAlone();
      } else {
        console.warn(
          'useInterviewRecorder[screen]: screenStream nulled but track still live -- keeping recording',
        );
      }
    }
  }, [screenStream, state, useComposite, startScreen, stopScreenAlone]);

  // ----------------------------------------------------------------
  // Auto-start once everything is ready and `enabled` flips on.
  //
  // Crucially we ALSO wait for `aiAudioContext` to exist before starting,
  // otherwise we'd fall back to a brand-new AudioContext and then never
  // be able to connect the AI tap into the recording mix (Web Audio
  // forbids connect() across contexts) -> recordings would only contain
  // the candidate mic, no AI voice.
  //
  // Safety net: if the audio hook never sets up the playback ctx (e.g.
  // worklet load failed), we still want to record the candidate side
  // after a grace period rather than recording nothing at all.
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!enabled) return;
    if (state !== 'idle') return;
    if (!sessionId || !cameraStream) return;
    if (aiAudioContext) {
      void start();
      return;
    }
    const fallbackTimer = setTimeout(() => {
      if (state === 'idle') {
        console.warn(
          'useInterviewRecorder: starting without AI audio ctx -- recording will only contain candidate mic',
        );
        void start();
      }
    }, 5000);
    return () => clearTimeout(fallbackTimer);
  }, [enabled, sessionId, cameraStream, state, start, aiAudioContext]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      for (const ref of [camRecorderRef, scrRecorderRef]) {
        const rec = ref.current;
        if (rec && rec.state !== 'inactive') {
          try { rec.stop(); } catch { /* ignore */ }
        }
      }
    };
  }, []);

  return {
    state,
    error,
    uploadedBytes,
    uploadHealth,
    start,
    stop,
    // Backwards-compat for ScreenSharePill consumers. These are now
    // playback-time concerns; the recorder no longer reads them.
    splitRatio,
    setSplitRatio,
    layout,
    setLayout,
  };
}
